import { initializeFaro, getWebInstrumentations, VERSION } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';
import { version as appVersion } from '../../../package.json';

// Global variable to store current user ID
let currentUserId = 'anonymous';

const isLocalDevelopment = (): boolean => {
  if (import.meta.env.MODE === 'development') {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const isFrontendObservabilityEnabled = (): boolean => {
  return import.meta.env.VITE_APP_FRONTEND_OBSERVABILITY_ENABLED === 'true' && !isLocalDevelopment();
};

// Function to update user ID (call this when user logs in)
/**
 *
 * @param userId
 */
export function setCurrentUserId(userId: string): void {
  currentUserId = userId;

  if (!isFrontendObservabilityEnabled()) {
    return;
  }

  // Send user_login event for "Top Users" panel (after login)
  if (userId !== 'anonymous') {
    const pushEventToLoki = async (eventType: string, data: Record<string, unknown>): Promise<void> => {
      const commonFields = {
        page_url: window.location.href,
        browser_name: (navigator as any).userAgentData?.brands?.[0]?.brand || navigator.userAgent,
        browser_version: (navigator as any).userAgentData?.brands?.[0]?.version || VERSION,
        app_version: appVersion
      };

      const toLogfmt = (fields: Record<string, unknown>): string => {
        const parts: string[] = [];
        Object.entries(fields).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          const value = typeof v === 'string' ? v.replace(/"/g, '\\"') : v;
          parts.push(`${k}="${value}"`);
        });
        return parts.join(' ');
      };

      const nowNanos = (): string => `${Date.now()}000000`;

      const eventLine = `${eventType} ${toLogfmt({ ...data, ...commonFields, user_id: userId })}`;
      const eventLabels = {
        Environment: import.meta.env.VITE_APP_ENV || 'development',
        Application: import.meta.env.VITE_APP_FARO_APPLICATION || 'MainSpa',
        kind: 'event'
      };

      const body = {
        streams: [
          {
            stream: eventLabels,
            values: [[nowNanos(), eventLine]]
          }
        ]
      };

      try {
        const faroCollectorUrl = (import.meta.env.VITE_APP_NESTJS_BASE_URL || '') + '/observability/metric_logs/push';
        const res = await fetch(faroCollectorUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true
        });
        if (!res.ok) {
          console.warn('Loki user_login push non-OK', res.status, res.statusText);
        }
      } catch (err) {
        console.warn('Loki user_login push failed', err);
      }
    };

    void pushEventToLoki('user_login', {
      event_name: 'user_login',
      user_id: userId,
      login_time: Date.now()
    });
  }
}

// Get current user ID
const getUserId = (): string => currentUserId;

/**
 *
 */
export function initializeFrontendObservability(): void {
  // Check if frontend observability is enabled
  const isEnabled = isFrontendObservabilityEnabled();
  if (!isEnabled) {
    return;
  }

  // Use NestJS proxy endpoint instead of direct Loki URL
  const faroCollectorUrl = (import.meta.env.VITE_APP_NESTJS_BASE_URL || '') + '/observability/metric_logs/push';
  const application = import.meta.env.VITE_APP_FARO_APPLICATION || 'MainSpa';
  const environment = import.meta.env.VITE_APP_ENV || 'development';

  // Initialize Faro for instrumentation only (no automatic transport)
  // We'll manually push to Loki using our custom functions below
  initializeFaro({
    url: faroCollectorUrl,
    app: {
      name: application,
      version: appVersion
    },
    dedupe: true,
    paused: true, // Pause automatic sending - we'll send manually
    ignoreErrors: [],
    instrumentations: [...getWebInstrumentations(), new TracingInstrumentation()]
  });

  // Manual Loki push for Grafana dashboard compatibility (logfmt measurements)
  const labels = {
    Environment: environment,
    Application: application,
    kind: 'measurement'
  } as const;

  const commonFields = {
    page_url: window.location.href,
    browser_name: (navigator as any).userAgentData?.brands?.[0]?.brand || navigator.userAgent,
    browser_version: (navigator as any).userAgentData?.brands?.[0]?.version || VERSION,
    app_version: appVersion
  };

  const toLogfmt = (fields: Record<string, unknown>): string => {
    const parts: string[] = [];
    Object.entries(fields).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      const value = typeof v === 'string' ? v.replace(/"/g, '\\"') : v;
      parts.push(`${k}="${value}` + `"`);
    });
    return parts.join(' ');
  };

  const nowNanos = (): string => `${Date.now()}000000`;

  const pushToLoki = async (line: string): Promise<void> => {
    const body = {
      streams: [
        {
          stream: labels,
          values: [[nowNanos(), line]]
        }
      ]
    };
    try {
      const res = await fetch(faroCollectorUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true
      });
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn('Loki push non-OK', res.status, res.statusText);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Loki push failed', err);
    }
  };

  // Web Vitals → Loki
  onTTFB((metric) => {
    void pushToLoki(`ttfb ${toLogfmt({ ttfb: Math.round(metric.value), ...commonFields })}`);
  });
  onFCP((metric) => {
    void pushToLoki(`fcp ${toLogfmt({ fcp: Math.round(metric.value), ...commonFields })}`);
  });
  onLCP((metric) => {
    void pushToLoki(`lcp ${toLogfmt({ lcp: Math.round(metric.value), ...commonFields })}`);
  });
  onCLS((metric) => {
    void pushToLoki(`cls ${toLogfmt({ cls: Number(metric.value.toFixed(3)), ...commonFields })}`);
  });
  onINP((metric) => {
    void pushToLoki(`inp ${toLogfmt({ inp: Math.round(metric.value), ...commonFields })}`);
  });

  // Capture Faro events (errors, user interactions, etc.)
  const pushEventToLoki = async (eventType: string, data: Record<string, unknown>): Promise<void> => {
    const eventLine = `${eventType} ${toLogfmt({ ...data, ...commonFields, user_id: getUserId() })}`;
    const eventLabels = { ...labels, kind: 'event' };

    const body = {
      streams: [
        {
          stream: eventLabels,
          values: [[nowNanos(), eventLine]]
        }
      ]
    };

    try {
      const res = await fetch(faroCollectorUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true
      });
      if (!res.ok) {
        console.warn('Loki event push non-OK', res.status, res.statusText);
      }
    } catch (err) {
      console.warn('Loki event push failed', err);
    }
  };

  // Send test line immediately
  void pushToLoki(`init faro_initialized=true ${toLogfmt(commonFields)}`);

  // Send mainspa_mount event for "Top Users" panel
  void pushEventToLoki('mainspa_mount', {
    event_name: 'mainspa_mount',
    user_id: getUserId(),
    session_id: Date.now().toString(),
    mount_time: Date.now()
  });

  // Capture JavaScript errors
  const pushErrorToLoki = async (errorType: string, data: Record<string, unknown>): Promise<void> => {
    const eventLine = `${errorType} ${toLogfmt({ ...data, ...commonFields, user_id: getUserId() })}`;
    const errorLabels = { ...labels, kind: 'error' };

    const body = {
      streams: [
        {
          stream: errorLabels,
          values: [[nowNanos(), eventLine]]
        }
      ]
    };

    try {
      const res = await fetch(faroCollectorUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true
      });
      if (!res.ok) {
        console.warn('Loki error push non-OK', res.status, res.statusText);
      }
    } catch (err) {
      console.warn('Loki error push failed', err);
    }
  };

  window.addEventListener('error', (event) => {
    void pushErrorToLoki('error', {
      error_message: event.message,
      error_filename: event.filename,
      error_lineno: event.lineno,
      error_colno: event.colno,
      error_stack: event.error?.stack
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    void pushErrorToLoki('unhandled_promise_rejection', {
      reason: event.reason?.toString(),
      promise: event.promise?.toString()
    });
  });

  // Don't track clicks - too noisy
  // Users can manually track important interactions using the exported functions

  // Capture form submissions
  document.addEventListener('submit', (event) => {
    const form = event.target as HTMLFormElement;
    if (form) {
      void pushEventToLoki('form_submit', {
        event_name: 'form_submit',
        form_id: form.id || '',
        form_class: form.className || '',
        form_action: form.action || '',
        page_url: window.location.href
      });
    }
  });

  // Capture page navigation (for SPA)
  let lastUrl = window.location.href;
  const observer = new MutationObserver(() => {
    if (window.location.href !== lastUrl) {
      void pushEventToLoki('page_navigation', {
        event_name: 'page_navigation',
        from_url: lastUrl,
        to_url: window.location.href,
        navigation_type: 'spa'
      });
      lastUrl = window.location.href;
    }
  });
  observer.observe(document, { subtree: true, childList: true });
}
